import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Box, Chip, Divider, IconButton, Stack, Typography } from '@mui/material'
import { FC, useMemo } from 'react'
import {
  AutomatedDeviceInfractionDetailsEnforcementLevel,
  CustomerCommunicationAction,
  CustomerCommunicationActionType,
  CustomerEscalationAction,
  CustomerEscalationActionType,
  FailureActionResultType,
  LocationFeature,
  SuccessActionResultType,
  UnenrollCameraAction,
  UnenrollCameraActionType
} from '../../../../api/models'
import { useLocation } from '../../../../api/apiQueries'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { Banner, BannerType } from '../../../../components/Banner/Banner'
import { BundledModerationEvent, getBundledEventsCountLabel, getEngagementReviewUrl, getEscalationLevel } from '../cameraOverviewUtils'
import { BadgeStatus } from '../cameraOverviewEnums'
import { BundledEventRow } from './BundledEventRow'

interface CameraHistoryItemDetailsProps {
  event: BundledModerationEvent
}

const channelDisplayName: Record<string, string> = {
  email: 'Email',
  sms: 'SMS',
  app: 'In-App Inbox'
}

const channelDescription: Record<string, string> = {
  email: 'Sent to location owner',
  sms: 'All location text recipients opted into system health notifications',
  app: 'Sent to location owner'
}

export const CameraHistoryItemDetails: FC<CameraHistoryItemDetailsProps> = ({ event }) => {
  const { toast } = useSnackbar()
  const locationQuery = useLocation(event.locationId)
  const location = locationQuery.data
  const bundledDuplicateClearanceEvents = event.bundledDuplicateClearanceEvents ?? []

  const qaToggleIsEnabled = useMemo(() => {
    const isActiveQaToggleFeature = (feature: LocationFeature) => {
      return feature.name === 'OperationalQualityAssuranceReviewEligible' && feature.enabled
    }
    return location?.features?.some(isActiveQaToggleFeature) ?? false
  }, [location])

  const manualLinks = event?.details && 'links' in event.details ? event.details.links : []
  const engagementReviewUrl = getEngagementReviewUrl(event)
  const links = engagementReviewUrl ? [engagementReviewUrl, ...manualLinks] : manualLinks

  const handleCopyClick = async (url: string | undefined) => {
    await navigator.clipboard.writeText(url ?? '')
    toast('Link copied.', { variant: 'success' })
  }

  const commAction = useMemo(
    () => event.actions?.find((a): a is CustomerCommunicationAction => a.type === CustomerCommunicationActionType.CustomerCommunication),
    [event.actions]
  )

  const unenrollAction = useMemo(
    () => event.actions?.find((a): a is UnenrollCameraAction => a.type === UnenrollCameraActionType.UnenrollCamera),
    [event.actions]
  )

  const escalationAction = useMemo(
    () => event.actions?.find((a): a is CustomerEscalationAction => a.type === CustomerEscalationActionType.CustomerEscalation),
    [event.actions]
  )

  const isFirstWarning =
    'enforcementLevel' in event.details && event.details.enforcementLevel === AutomatedDeviceInfractionDetailsEnforcementLevel.FirstWarning

  const channelEntries = useMemo(() => {
    if (!commAction) return []
    const entries = Object.entries(commAction.content ?? {}).filter(([key, v]) => v !== undefined && key in channelDisplayName)
    return isFirstWarning ? entries.filter(([key]) => key !== 'sms') : entries
  }, [commAction, isFirstWarning])

  const isUnenrollLevel = getEscalationLevel(event) === BadgeStatus.Unenrolled

  const shouldShowOceoTicket = !!escalationAction

  const escalationTicketKey = escalationAction?.result?.type === SuccessActionResultType.Success ? escalationAction.result.ticketKey : undefined

  const escalationFailed =
    escalationAction?.result?.type === FailureActionResultType.Failure ||
    (escalationAction?.result?.type === SuccessActionResultType.Success && !escalationTicketKey)

  const hasActionRows = channelEntries.length > 0 || (isUnenrollLevel && !!unenrollAction) || shouldShowOceoTicket

  const actionRowSx = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'var(--bg-page)',
    px: 2,
    height: '2.5rem'
  }

  return (
    <>
      <Box display="flex" gap={4} flexWrap="wrap" sx={{ borderTop: '1px solid var(--bg-surface-low)', paddingTop: 'var(--spacing-s)' }}>
        {/* Left Column */}
        <Stack gap={1} flex={1} minWidth={0}>
          <Box>
            <Typography fontWeight="bold" mb={1}>
              Details
            </Typography>
            <Box sx={{ backgroundColor: 'var(--bg-page)', borderRadius: 'var(--border-radius-l)', padding: 'var(--spacing-s)' }}>
              <Typography
                variant="body2"
                whiteSpace="pre-line"
                sx={{
                  color: 'var(--text-secondary)',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  hyphens: 'auto'
                }}
              >
                {event?.details?.description ?? 'No description available.'}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography fontWeight="bold" mb={1}>
              Links
            </Typography>
            {links.length > 0 ? (
              <Stack spacing={'var(--spacing-3xs)'}>
                {links.map((url: string, index: number) => (
                  <Box
                    key={index}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      backgroundColor: 'var(--bg-page)',
                      padding: 'var(--spacing-2xs)',
                      paddingLeft: 'var(--spacing-s)',
                      borderRadius: 'var(--border-radius-l)'
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 'var(--size-xs)',
                        color: 'var(--text-secondary)',
                        fontWeight: 500,
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        marginRight: '0.625rem'
                      }}
                    >
                      {url}
                    </Typography>
                    <IconButton size="small">
                      <ContentCopyIcon onClick={() => handleCopyClick(url)} fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                No links available.
              </Typography>
            )}
          </Box>
        </Stack>

        {/* Right Column — Actions */}
        <Box flex={2} minWidth="18.75rem">
          <Typography fontWeight="bold" mb={1}>
            Actions
          </Typography>
          {hasActionRows ? (
            <Stack gap="var(--spacing-3xs)">
              {channelEntries.length > 0 && (
                <Box sx={{ border: '1px solid var(--bg-surface-low)', borderRadius: 'var(--border-radius-l)', overflow: 'hidden' }}>
                  {commAction?.result?.type === FailureActionResultType.Failure && (
                    <Banner
                      type={BannerType.Error}
                      title="Sending communications failed."
                      body="Resubmit manually by clicking take action on the correct camera."
                      sx={{
                        borderRadius: 0,
                        fontSize: 'var(--size-xs)',
                        '& .MuiAlertTitle-root': { fontSize: 'var(--size-xs)', marginBottom: 'var(--spacing-none)' },
                        '& .MuiAlert-icon': { alignSelf: 'center' }
                      }}
                      data-testid={`communication-failure-banner-${event.id}`}
                    />
                  )}
                  {channelEntries.map(([key], index) => (
                    <Box key={key}>
                      {index > 0 && <Divider />}
                      <Box sx={{ ...actionRowSx, justifyContent: 'flex-start', gap: 'var(--spacing-l)' }}>
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: 'var(--size-2xs)', width: '5rem', flexShrink: 0 }}>
                          {channelDisplayName[key]}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: 'var(--size-2xs)', color: 'var(--text-secondary)' }}>
                          {channelDescription[key]}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {isUnenrollLevel && unenrollAction && (
                <Box sx={{ border: '1px solid var(--bg-surface-low)', borderRadius: 'var(--border-radius-l)', overflow: 'hidden' }}>
                  {unenrollAction.result?.type === FailureActionResultType.Failure && (
                    <Banner
                      type={BannerType.Error}
                      title="Unenrollment failed."
                      body="Resubmit manually by clicking take action on the correct camera."
                      sx={{
                        borderRadius: 0,
                        fontSize: 'var(--size-xs)',
                        '& .MuiAlertTitle-root': { fontSize: 'var(--size-xs)', marginBottom: 'var(--spacing-none)' },
                        '& .MuiAlert-icon': { alignSelf: 'center' }
                      }}
                      data-testid={`unenroll-failure-banner-${event.id}`}
                    />
                  )}
                  <Box sx={actionRowSx}>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: 'var(--size-2xs)' }}>
                      Unenrolled
                    </Typography>
                  </Box>
                </Box>
              )}

              {shouldShowOceoTicket && (
                <Box sx={{ border: '1px solid var(--bg-surface-low)', borderRadius: 'var(--border-radius-l)', overflow: 'hidden' }}>
                  {escalationFailed && (
                    <Banner
                      type={BannerType.Error}
                      title="OCEO ticket wasn't created."
                      body="Resubmit manually by clicking take action on the correct camera."
                      sx={{
                        borderRadius: 0,
                        fontSize: 'var(--size-xs)',
                        '& .MuiAlertTitle-root': { fontSize: 'var(--size-xs)', marginBottom: 'var(--spacing-none)' },
                        '& .MuiAlert-icon': { alignSelf: 'center' }
                      }}
                      data-testid={`escalation-failure-banner-${event.id}`}
                    />
                  )}
                  <Box sx={{ ...actionRowSx, justifyContent: 'flex-start', gap: 'var(--spacing-l)' }}>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: 'var(--size-2xs)', width: '5rem', flexShrink: 0 }}>
                      OCEO Ticket
                    </Typography>
                    {escalationTicketKey && (
                      <Typography variant="body2" sx={{ fontSize: 'var(--size-2xs)', color: 'var(--text-secondary)' }}>
                        {escalationTicketKey}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Stack>
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              sx={{ backgroundColor: 'var(--bg-page)', borderRadius: 'var(--border-radius-l)', padding: 'var(--spacing-s)' }}
            >
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                No actions supported for this type.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', alignContent: 'center', mt: 1 }} gap={1}>
        <Typography sx={{ fontWeight: 'bold', fontSize: 'var(--size-xs)' }}>QA Toggle</Typography>
        <Chip
          label={qaToggleIsEnabled ? 'On' : 'Off'}
          size="small"
          sx={{
            height: 24,
            borderRadius: 'var(--spacing-2xs)',
            opacity: 1,
            gap: 'var(--spacing-2xs)',
            paddingLeft: 'var(--spacing-2xs)',
            paddingRight: 'var(--spacing-2xs)',
            backgroundColor: qaToggleIsEnabled ? 'var(--shape-confirm-transparent)' : 'var(--shape-medium)',
            color: 'var(--neutral-900)',
            fontSize: 'var(--size-s)'
          }}
        />
      </Box>

      {bundledDuplicateClearanceEvents.length > 0 && (
        <Box data-testid={`bundled-events-section-${event.id}`} sx={{ mt: 2 }}>
          <Typography fontWeight="bold" mb={1}>
            {getBundledEventsCountLabel(bundledDuplicateClearanceEvents.length)}
          </Typography>
          <Stack gap="var(--spacing-3xs)">
            {bundledDuplicateClearanceEvents.map(child => (
              <BundledEventRow key={child.id} event={child} />
            ))}
          </Stack>
        </Box>
      )}
    </>
  )
}
